import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography
} from '@material-ui/core'
import Select from 'react-select'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'
import withSharedSnackbar from '../../HOCs/withSharedSnackbar'

// GraphQL Queries and Mutations
const GET_BRANDS = gql`
  query GetBrands($clientLoginId: Int!) {
    getBrands(clientLoginId: $clientLoginId) {
      status
      message
      data {
        id
        name
      }
    }
  }
`

const CREATE_MODEL = gql`
  mutation CreateModel(
    $name: String!
    $truckBrandId: Int!
    $clientLoginId: Int!
    $engineHours: Float
    $feVariable: Float
  ) {
    createModel(
      name: $name
      truckBrandId: $truckBrandId
      clientLoginId: $clientLoginId
      engineHours: $engineHours
      feVariable: $feVariable
    ) {
      status
      message
      data {
        name
        client_login_id
        truck_brand_id
        engine_hours
        fe_variable
      }
    }
  }
`

const AddModelModal = ({
  open,
  onClose,
  clientLoginId,
  getBrands,
  createModel,
  openSnackbar
}) => {
  const [name, setName] = useState('')
  const [truckBrandName, setTruckBrandName] = useState('')
  const [truckBrandId, setTruckBrandId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState({})
  const [searchTerm, setSearchTerm] = useState('')
  const [engineHours, setEngineHours] = useState('')
  const [feVariable, setFeVariable] = useState('')
  const brandsData =
    getBrands && getBrands.getBrands ? getBrands.getBrands.data : []
  const { loading: brandsLoading, refetch } = getBrands

  useEffect(
    () => {
      if (open) {
        refetch()
      }
    },
    [open, refetch]
  )

  const handleSave = () => {
    setLoading(true)
    const validationErrors = {}
    if (!name.trim()) validationErrors.name = 'Model name is required.'
    if (!truckBrandId)
      validationErrors.truckBrandId = 'Please select a truck brand.'
    if (truckBrandName === 'volvo') {
      if (isNaN(parseFloat(engineHours))) {
        validationErrors.engineHours = 'Engine hours must be a valid number.'
      } else if (engineHours.replace(/[^0-9]/g, '').length > 5) {
        validationErrors.engineHours = 'Engine hours must not exceed 3 digits.'
      }

      if (isNaN(parseFloat(feVariable))) {
        validationErrors.feVariable = 'FE Variable must be a valid number.'
      } else if (feVariable.replace(/[^0-9]/g, '').length > 3) {
        validationErrors.feVariable = 'FE Variable must not exceed 3 digits.'
      }
    }
    if (Object.keys(validationErrors).length > 0) {
      setError(validationErrors)
      setLoading(false)
      return
    }
    if (truckBrandName === 'volvo' && isNaN(parseFloat(feVariable))) {
      validationErrors.feVariable = 'FE Variable must be a valid number.'
    }

    createModel({
      variables: {
        name,
        truckBrandId,
        clientLoginId,
        engineHours: engineHours ? parseFloat(engineHours) : null,
        feVariable: truckBrandName === 'volvo' && feVariable
          ? parseFloat(feVariable)
          : null,
      },
      refetchQueries: [{ query: GET_BRANDS, variables: { clientLoginId } }],
      awaitRefetchQueries: true
    })
      .then(response => {
        if (response.data.createModel.status) {
          handleReset()
          onClose()
          openSnackbar(response.data.createModel.message, {
            variant: 'success'
          })
        } else {
          openSnackbar(response.data.createModel.message, { variant: 'error' })
        }
        setLoading(false)
      })
      .catch(error => {
        console.error(`Error: ${error.message}`)
        setLoading(false)
      })
  }

  const handleReset = () => {
    setName('')
    setTruckBrandId(null)
    setTruckBrandName('')
    setError({})
    setFeVariable('')
  }

  const handleCancel = () => {
    handleReset()
    onClose()
  }

  const brandOptions = brandsData.map(brand => ({
    value: brand.id,
    label: brand.name
  }))

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: { overflow: 'visible' }
      }}
    >
      <DialogTitle>Add New Model</DialogTitle>
      <DialogContent>
        <div style={{ padding: '10px' }}>
          <TextField
            fullWidth
            label="Model Name"
            variant="outlined"
            value={name}
            onChange={e => {
              setName(e.target.value)
              setError(prev => ({ ...prev, name: '' }))
            }}
            error={!!error.name}
            helperText={error.name}
            style={{ marginBottom: '16px' }}
          />

          <div style={{ marginTop: '16px' }}>
            <Typography variant="subtitle1" gutterBottom>
              Truck Brand
            </Typography>
            <Select
              options={brandOptions}
              onChange={selectedOption => {
                setTruckBrandId(selectedOption ? selectedOption.value : null)
                setTruckBrandName(selectedOption ? selectedOption.label.toLowerCase() : '')
                setError(prev => ({ ...prev, truckBrandId: '' }))
              }}
              value={
                brandOptions.find(option => option.value === truckBrandId) ||
                null
              }
              placeholder="Select Truck Brand"
              isLoading={brandsLoading}
              getOptionLabel={option => option.label}
              getOptionValue={option => option.value}
              styles={{
                container: base => ({
                  ...base
                })
              }}
            />

          </div>
        </div>
        {error.truckBrandId && (
          <div style={{ color: 'red', marginBottom: 16 }}>
            {error.truckBrandId}
          </div>
        )}
        {truckBrandName === 'volvo' && (
          <div>
            <TextField
              fullWidth
              label="Engine Hours"
              variant="outlined"
              value={engineHours}
              onChange={e => {
                const value = e.target.value
                const numericOnly = value.replace(/[^0-9]/g, '')
                const isValidFormat = /^\d*\.?\d*$/.test(value)
                if (isValidFormat && numericOnly.length <= 5) {
                  setEngineHours(value)
                  setError(prev => ({ ...prev, engineHours: '' }))
                }
              }}
              error={!!error.engineHours}
              helperText={error.engineHours}
              style={{ marginBottom: '16px' }}
            />

            <TextField
              fullWidth
              label="FE Variable"
              variant="outlined"
              value={feVariable}
              onChange={e => {
                const value = e.target.value
                const numericOnly = value.replace(/[^0-9]/g, '')
                const isValidFormat = /^\d*\.?\d*$/.test(value)
                if (isValidFormat && numericOnly.length <= 3) {
                  setFeVariable(value)
                  setError(prev => ({ ...prev, feVariable: '' }))
                }
              }}
              error={!!error.feVariable}
              helperText={error.feVariable}
              style={{ marginBottom: '16px' }}
            />

          </div>
        )}
      </DialogContent>
      <DialogActions style={{ paddingRight: '20px', marginBottom: '10px' }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          style={{
            borderColor: '#f44336',
            color: '#f44336',
            borderRadius: 8
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={loading}
          style={{
            backgroundColor: '#66bb6a',
            color: '#ffffff',
            borderRadius: 8,

            '&:hover': {
              backgroundColor: '#81c784'
            }
          }}
        >
          {loading ? 'Saving...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default graphql(GET_BRANDS, {
  name: 'getBrands',
  options: props => ({
    variables: { clientLoginId: props.clientLoginId }
  })
})(
  graphql(CREATE_MODEL, {
    name: 'createModel'
  })(withSharedSnackbar(AddModelModal))
)



// WEBPACK FOOTER //
// ./src/components/Pages/modalConfigure/AddModelModal.jsx